import {
  SharedTooltipContext,
  TooltipPoint,
} from "@progress/kendo-react-charts";
import s from "./TimeSeriesSharedTooltipRenderer.module.scss";
import classNames from "classnames";

export const TimeSeriesSharedTooltipRenderer = (
  info: SharedTooltipContext,
  valueFormatter?: (v?: number) => string,
  categoryFormatter?: (v?: string) => string,
  settings?: StandardTimeSeriesTooltipContentProps["settings"]
) => {
  return (
    <StandardTimeSeriesTooltipContent
      info={info}
      valueFormatter={valueFormatter}
      categoryFormatter={categoryFormatter}
      settings={settings}
    />
  );
};

export type TooltipSettings = {
  hideTotal?: boolean;
  showZeroes?: boolean;
};

type StandardTimeSeriesTooltipContentProps = {
  info: SharedTooltipContext;
  valueFormatter?: (v?: number) => string;
  categoryFormatter?: (v?: string, ev?: any) => string;
  settings?: TooltipSettings;
};

export const StandardTimeSeriesTooltipContent = ({
  info,
  categoryFormatter,
  valueFormatter,
  settings,
}: StandardTimeSeriesTooltipContentProps) => {
  const showTotal = !settings?.hideTotal;
  const total = showTotal
    ? info.points
        .filter(
          (point) =>
            point.series.type !== "candlestick" &&
            point.series.type !== "rangeArea"
        )
        .reduce((acc, curr) => acc + (curr.value as number), 0)
    : undefined;

  const nonZeroPoints = settings?.showZeroes
    ? info?.points
    : info?.points.filter((point) => {
        // For candlestick and rangeArea series, always show them (they don't have simple numeric values)
        if (
          point.series.type === "candlestick" ||
          point.series.type === "rangeArea"
        ) {
          return true;
        }
        return point.value !== 0;
      });

  const formatTooltipPoint = (point: TooltipPoint) => {
    // For candlestick series, point.value is an object, not a number
    if (point.series.type === "candlestick") {
      return "Candlestick"; // Candlestick values are handled separately in the render
    }
    return valueFormatter
      ? valueFormatter?.(point.value as number)
      : point.value;
  };

  const totalString = valueFormatter ? valueFormatter?.(total || 0) : total;

  const formattedCategoryText = categoryFormatter
    ? categoryFormatter(info.categoryText)
    : info.categoryText;

  return (
    <div>
      <h4 className={s.pointCategory}>{formattedCategoryText}</h4>
      <div className={classNames(s.spaceBetween, "tooltip-content")}>
        {showTotal && `Total: ${totalString}`}
      </div>
      {nonZeroPoints.map((point, key) => {
        const isPointCandlestick = point.series.type === "candlestick";
        const isPointRangeArea = point.series.type === "rangeArea";

        return (
          <div key={key}>
            {isPointCandlestick ? (
              <div>
                {["Open", "Close", "High", "Low"].map((field) => (
                  <div key={field} className={classNames(s.row)}>
                    <b>{field} :</b>
                    <div className={s.value}>
                      {point.value[
                        field.toLowerCase() as keyof typeof point.value
                      ].toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ) : isPointRangeArea ? (
              <div className={classNames(s.spaceBetween, "tooltip-content")}>
                <div className={s.row}>
                  <div
                    className={s.point}
                    style={{ background: (point as any).point.color }}
                  />
                  <div>{point.series.name}</div>
                  <div className={s.value}>
                    {point.value.from.toFixed(1)} - {point.value.to.toFixed(1)}
                  </div>
                </div>
              </div>
            ) : (
              <div className={classNames(s.spaceBetween, "tooltip-content")}>
                <div className={s.row}>
                  <div
                    className={s.point}
                    style={{ background: (point as any).point.color }}
                  />
                  <div>{point.series.name}</div>
                  <div className={s.value}>{formatTooltipPoint(point)}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
